import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthTokenStore } from '../stores/authToken';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // v2.5.0 (Ship 11, M-3) — send the httpOnly ``adaptcx_refresh`` cookie
  // on requests to auth-service. Required for /refresh and /logout across
  // portal subdomains that share the auth cookie domain.
  withCredentials: true,
});

/**
 * v2.5.0 (Ship 11, M-3) — clear the in-memory access token AND sweep any
 * legacy storage entries. The one-release sweep is defensive residue
 * cleanup on users' devices that had pre-Ship-11 tokens.
 */
function clearAuthAndRedirect(): void {
  try {
    useAuthTokenStore().clear();
  } catch {
    // Pinia not initialized yet — non-fatal.
  }
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth_storage_type');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('auth_storage_type');

  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login?session_expired=true';
  }
}

/**
 * Read the current access token from the Pinia store. Returns null if
 * the store isn't initialized (rare — bootstrap error) or the user
 * isn't authenticated.
 */
function getToken(): string | null {
  try {
    return useAuthTokenStore().accessToken;
  } catch {
    return null;
  }
}

// v2.5.0 (Ship 11, M-3) — single-flight refresh lock. Prevents N concurrent
// 401s from stampeding N /refresh calls at auth-service.
let _refreshInflight: Promise<string | null> | null = null;

async function _refreshAccessToken(): Promise<string | null> {
  if (_refreshInflight) return _refreshInflight;
  _refreshInflight = (async () => {
    try {
      const resp = await axios.post(
        `${API_BASE_URL}/api/v1/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const newAccess = resp.data?.access_token as string | undefined;
      if (!newAccess) return null;
      try {
        // Decode exp for store (best-effort).
        const parts = newAccess.split('.');
        let expiresAt: number | null = null;
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
          if (payload?.exp) expiresAt = payload.exp * 1000;
        }
        useAuthTokenStore().set(newAccess, expiresAt);
      } catch {
        // Store update failed (Pinia not ready) — token still returned to caller.
      }
      return newAccess;
    } catch {
      return null;
    }
  })().finally(() => {
    _refreshInflight = null;
  });
  return _refreshInflight;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // v2.5.0 (Ship 11, M-3) — never retry /refresh itself; that path is
    // its own escape hatch. A 401 on /refresh means the cookie is stale
    // or invalid — fall through to the redirect.
    const url = originalRequest?.url || '';
    const isRefreshCall = url.includes('/api/v1/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshCall) {
      originalRequest._retry = true;

      const newAccess = await _refreshAccessToken();
      if (newAccess) {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        }
        return api(originalRequest);
      }
      clearAuthAndRedirect();
    }

    // Phase 2.20 — 402 quota_exceeded handler. The api-gateway issues
    // this when the backend's enforce_or_402 raises on a hard_cap plan.
    // We hand the payload to the shared quotaStore so any portal-level
    // banner (Phase 2.22) can render the exceeded state without each
    // view having to handle 402 individually.
    if (error.response?.status === 402 && (error.response?.data as any)?.code === 'quota_exceeded') {
      try {
        // Lazy import — quotaStore needs Pinia which isn't installed
        // in non-Vue consumers (e.g. headless tests). Wrap so api.ts
        // stays importable everywhere.
        const { useQuotaStore } = await import('../stores/quotaStore');
        const store = useQuotaStore();
        store.markExceeded(error.response.data as any);
      } catch {
        // Pinia not set up yet (e.g. cold-load) — non-fatal.
      }
    }

    return Promise.reject(error);
  }
);

// Alias for convenience
export const apiClient = api;

// Export the clear function for use by auth service
export { clearAuthAndRedirect };

export default api;
